/**
 * Workflow ↔ Monday.com column mapping
 *
 * SPLIT ARCHITECTURE:
 *   Estimates board (18396590426) — 16 labels (Marketing + Venta phases)
 *   Miami board (18396594554)     — 40 labels (Preliminar + Documentos + Suspendido)
 *   Dallas board (18396592406)    — 40 labels (same as Miami)
 *
 * Monday dropdown label IDs are 1-based on the new columns.
 */
import { warnMissingFields } from '../utils/devAssert'

/**
 * @typedef {Object} ArchProject
 * @property {string}      id            — Monday item ID
 * @property {string}      name          — address / item title
 * @property {string}      boardId
 * @property {string}      boardName
 * @property {string}      groupId
 * @property {string}      groupTitle
 * @property {number|null} statusWfId    — Status Workflow dropdown ID
 * @property {string}      statusWfText
 * @property {number|null} statusPmId    — Status PM dropdown ID
 * @property {string}      statusPmText
 * @property {string}      clientName
 * @property {string}      clientEmail
 * @property {string}      office
 * @property {string}      driveLink
 * @property {string}      pmName
 * @property {number|null} pmUserId
 */

const _ARCH_FIELDS = [
    'id', 'name', 'boardId', 'boardName', 'groupId', 'groupTitle',
    'statusWfId', 'statusWfText', 'statusPmId', 'statusPmText',
    'clientName', 'clientEmail', 'office', 'driveLink', 'pmName', 'pmUserId',
]

// ── Board IDs (2026 only) ────────────────────────────────────────────────────
export const ESTIMATES_BOARD_ID = '18396590426'
export const PROJECT_BOARD_IDS = [
    '18396594554', // Project Miami 2026
    '18396592406', // Project Dallas 2026
]
export const ALL_WF_BOARD_IDS = [ESTIMATES_BOARD_ID, ...PROJECT_BOARD_IDS]

// ── Column IDs ───────────────────────────────────────────────────────────────
export const STATUS_WORKFLOW_COLUMNS = {
    '18396590426': 'dropdown_mm5kc3kb', // Estimates board — 16 labels
    '18396594554': 'dropdown_mm5ktwwm', // Project Miami 2026 — 40 labels
    '18396592406': 'dropdown_mm5kpbwz', // Project Dallas 2026 — 40 labels
}
export const STATUS_PM_COLUMN      = 'dropdown_mktas988'  // legacy — kept for fallback
export const CLIENT_EMAIL_COL      = 'text_mkt9qv82'
export const CLIENT_NAME_COL       = 'text_mkt9adtr'
export const OFFICE_COLUMN         = 'color_mkt9jxhh'
export const PM_COLUMN             = 'multiple_person_mktankd5'
export const LINK_COLUMNS = {
    '18396590426': ['link_mktb12zq', 'link_mktbwb'], // Estimates 2026 → [Link Miami, Link Dallas]
    '18396594554': ['link_mktc66fz'],                // Project Miami 2026 → "Link Miami"
    '18396592406': ['link_mktcek97'],                // Project Dallas 2026 → "Link Dallas"
}

// ── ESTIMATE_PM_FLOW indices (0-based, 16 cards) ────────────────────────────
//  0  1. Prospecto                  Phase 0 — Marketing
//  1  2. Primera Llamada
//  2  3. No Contestó
//  3  4. Llamada Programada
//  4  5. No Calificó
//  5  6. No Dio Información
//  6  7. Solicitud de Estimado       Phase 1 — Venta
//  7  8. Revisión Inicial
//  8  9. Visita Requerida
//  9  10. En Cotización / Estimado
// 10  11. Presentación de Venta
// 11  12. En Cotización Formal
// 12  13. A Cierre de Venta
// 13  14. Descartado
// 14  15. Firma de Contrato
// 15  16. Primer Pago (→ triggers transition to Projects board)

// ── PROJECT_PM_FLOW indices (0-based, 39 cards + Suspendido) ────────────────
//  0  1. PM Da la Bienvenida y Coord Visita     Phase 0 — Preliminar
//  1  2. PM Da la Bienvenida y/o En Prescreen
//  2  3. Producción Preliminar
//  3  4. PM Envía a Producción Preliminar
//  4  5. En Revisión de Preliminar por PM
//  5  6. 1er Envío Preliminar a Cliente
//  6  7. Cliente Recibe para Aprobación (1ra)
//  7  8. PM Recibe Aprobación Preliminar (1ra)
//  8  9. PM Recibe Correcciones Preliminar (1ra)
//  9  10. En Corrección Preliminar (2da)
// 10  11. 2do Envío Preliminar a Cliente
// 11  12. Cliente Recibe para Aprobación (2da)
// 12  13. PM Recibe Aprobación Preliminar (2da)
// 13  14. PM Recibe Correcciones Preliminar (2da)
// 14  15. En Corrección Preliminar (3ra)
// 15  16. 3er Envío Preliminar a Cliente
// 16  17. Cliente Recibe para Aprobación (3ra)
// 17  18. PM Recibe Aprobación Preliminar (3ra)
// 18  19. Solicitud de 2do Pago a Cliente
// 19  20. PM Da Instrucciones de CD (1ra)         Phase 1 — Documentos
// 20  21. En Producción por Proyectistas (1ra)
// 21  22. En 1era Revisión PM de CD
// 22  23. Revisión de Cada Disciplina / Certificación (1ra)
// 23  24. PM Envió Preliminar CD al Cliente
// 24  25. PM Recibe Aprobación de Cliente CD
// 25  26. PM Recibe Correcciones de Cliente CD
// 26  27. PM Da Instrucciones de CD (2da)
// 27  28. En Producción por Proyectistas (2da)
// 28  29. 2da Revisión PM de CD
// 29  30. 2da Revisión de Certificación
// 30  31. PM 2do Envío Preliminar CD al Cliente
// 31  32. Cliente Aprueba CD
// 32  33. PM Revisa y Solicita 3er Pago CD
// 33  34. Cliente Paga CD
// 34  35. PM Envía a Certificación Final
// 35  36. En Certificación Final
// 36  37. PM Recibe Certificación Final / Control de Calidad
// 37  38. PM Empaqueta y Envía
// 38  39. Cliente Recibe Conforme y/o Aprueba Someter a la Ciudad
//     40. Suspendido → −1

// ── PROJECT_CLIENT_FLOW indices (0-based, 12 cards) ──────────────────────────
//  0  Firma del Contrato / Bienvenida
//  1  Producción Preliminar
//  2  PM Revisión
//  3  Revisión Cliente (Preliminar)
//  4  Aprobación / Correcciones
//  5  Aprobación y 2do Pago
//  6  Producción DC
//  7  Envío DC / Aprobación Cliente
//  8  Último Pago
//  9  Certificación Final
// 10  Entrega Final
// 11  (reserved)

// ── Estimates board: Monday ID N → ESTIMATE_PM_FLOW index N-1 (1-based IDs) ─
export const ESTIMATE_WF_TO_PM_STEP = {}
for (let i = 1; i <= 16; i++) ESTIMATE_WF_TO_PM_STEP[i] = i - 1

// ── Projects board: Monday ID N → PROJECT_PM_FLOW index N-1 (1-based IDs) ────
// Labels 1–39 map to indices 0–38. Label 40 = Suspendido → -1.
export const PROJECT_WF_TO_PM_STEP = {}
for (let i = 1; i <= 39; i++) PROJECT_WF_TO_PM_STEP[i] = i - 1
PROJECT_WF_TO_PM_STEP[40] = -1 // Suspendido

// ── Projects board: Monday ID N → PROJECT_CLIENT_FLOW step (12 client nodes) ─
export const PROJECT_WF_TO_CLIENT_STEP = {
    // Phase 0 — Preliminar (clients see from Bienvenida onwards)
    1:  0,  // PM Da la Bienvenida → c0 (Firma/Bienvenida)
    2:  0,  // Prescreen → c0
    3:  1,  // Producción Preliminar → c1
    4:  1,  // PM Envía → c1
    5:  2,  // Revisión PM → c2
    6:  3,  // 1er Envío → c3 (Revisión Cliente)
    7:  4,  // Cliente Recibe (1ra) → c4 (Aprobación/Correcciones)
    8:  4,  // PM Recibe Aprobación (1ra) → c4
    9:  4,  // PM Recibe Correcciones (1ra) → c4
    10: 4,  // Corrección 2da → c4
    11: 4,  // 2do Envío → c4
    12: 4,  // Cliente Recibe (2da) → c4
    13: 4,  // PM Recibe Aprobación (2da) → c4
    14: 4,  // PM Recibe Correcciones (2da) → c4
    15: 4,  // Corrección 3ra → c4
    16: 4,  // 3er Envío → c4
    17: 4,  // Cliente Recibe (3ra) → c4
    18: 4,  // PM Recibe Aprobación (3ra) → c4
    19: 5,  // Solicitud 2do Pago → c5 (Aprobación y 2do Pago)
    // Phase 1 — Documentos
    20: 6,  // PM Instrucciones CD → c6 (Producción DC)
    21: 6,  // En Producción → c6
    22: 6,  // Revisión PM CD → c6
    23: 6,  // Certificación 1era → c6
    24: 7,  // PM Envió CD → c7 (Envío DC / Aprobación)
    25: 7,  // PM Recibe Aprobación CD → c7
    26: 7,  // PM Recibe Correcciones CD → c7
    27: 7,  // PM Instrucciones 2da → c7
    28: 7,  // En Producción 2da → c7
    29: 7,  // 2da Revisión PM → c7
    30: 7,  // 2da Revisión Certificación → c7
    31: 7,  // PM 2do Envío → c7
    32: 7,  // Cliente Aprueba CD → c7
    33: 8,  // PM Revisa 3er Pago → c8 (Último Pago)
    34: 8,  // Cliente Paga CD → c8
    35: 9,  // PM Envía Certificación → c9 (Certificación Final)
    36: 9,  // En Certificación Final → c9
    37: 9,  // PM Recibe Certificación → c9
    38: 10, // PM Empaqueta → c10 (Entrega)
    39: 10, // Cliente Recibe Conforme → c10
    // Suspendido
    40: -1,
}

// ── Workflow label keys (for resolving dropdown IDs to i18n keys) ───────────
// Monday.com API returns text:null for dropdown columns, so we must resolve
// the label ourselves from the dropdown ID.
const ESTIMATE_WF_LABEL_KEYS = []
for (let i = 1; i <= 16; i++) ESTIMATE_WF_LABEL_KEYS[i] = `admin.workflow.est_e${i}`

const PROJECT_WF_LABEL_KEYS = []
for (let i = 1; i <= 40; i++) PROJECT_WF_LABEL_KEYS[i] = `admin.workflow.pr_p${i}`
PROJECT_WF_LABEL_KEYS[40] = 'admin.workflow.suspended' // 40. Suspendido overrides pr_p40

/**
 * Resolve the i18n label key for a workflow dropdown ID on a given board.
 * @param {string} boardId — Monday board ID
 * @param {number} wfId — Monday dropdown label ID (1-based)
 * @returns {string} — i18n key like 'admin.workflow.est_e3' or '' if not found
 */
export function resolveWfLabelKey(boardId, wfId) {
    if (wfId == null) return ''
    const id = Number(wfId)
    if (boardId === ESTIMATES_BOARD_ID) {
        return ESTIMATE_WF_LABEL_KEYS[id] || ''
    }
    return PROJECT_WF_LABEL_KEYS[id] || ''
}

// Legacy aliases for backward compatibility (workflowTimers.js etc.)
export const STATUS_WF_TO_PM_STEP = PROJECT_WF_TO_PM_STEP
export const STATUS_WF_TO_CLIENT_STEP = PROJECT_WF_TO_CLIENT_STEP

// ── Legacy: Status PM → step (kept for old items not yet on Workflow column) ─
// These map the old Status PM column (dropdown_mktas988) to the new PROJECT flow indices.
const LEGACY_STATUS_TO_PM_STEP = {
    1:  2,  // En curso → Producción Preliminar (index 2)
    2:  1,  // En cola de produccion → Prescreen (index 1)
    3:  6,  // Esperando por el cliente → Cliente Recibe (1ra) (index 6)
    4:  4,  // En revision → Revisión PM (index 4)
    5:  18, // Esperando pago → Solicitud 2do Pago (index 18, disambiguate via group)
    6:  38, // Finalizado → Cliente Recibe Conforme (index 38)
    7:  23, // En procesamiento de permiso → PM Envió CD (index 23)
    8:  -1, // Suspendido
    9:  2,  // Abordando comentarios → Producción Preliminar
    10: 22, // En espera de inspeccion → Certificación (index 22)
    11: 35, // Certificado → En Certificación Final (index 35)
    12: 1,  // Prescreen PM
    13: 2,  // En producción preliminar
    14: 6,  // Esperando aprobación cliente (preliminar)
    15: 20, // En producción CD → En Producción por Proyectistas (index 20)
    16: 24, // Esperando aprobación cliente (CD) → PM Recibe Aprobación CD (index 24)
    17: 22, // En certificación → Certificación (index 22)
    18: 2,  // En re-cotización → Producción Preliminar
    19: 38, // Entrega final → Cliente Recibe Conforme (index 38)
}

const LEGACY_STATUS_TO_CLIENT_STEP = {
    1:  1, 2:  0, 3:  4, 4:  2, 5:  5,
    6: 10, 7:  7, 8: -1, 9:  1, 10: 9,
    11: 9, 12: 0, 13: 1, 14: 4, 15: 6,
    16: 7, 17: 9, 18: 1, 19: 10,
}

// ── Group fallback (when Workflow column is null) ───────────────────────────
// Maps actual Monday group IDs (Miami + Dallas variants) to PM flow step.
// Miami: topics, group_mktcbe6z, group_mktaggtb, group_mktyejw6, group_mktad09a, group_mktatkyf, group_mktazt64
// Dallas: topics, group_mktckxx5, group_mktaggtb, group_mkty3x75, group_mktad09a, group_mktatkyf, group_mktazt64
const GROUP_TO_PM_STEP = {
    'topics':           2,  // Activos Preliminar → Producción Preliminar
    'group_mktcbe6z':   20, // Activos CDs (Miami) → En Producción por Proyectistas
    'group_mktckxx5':   20, // Activos CDs (Dallas)
    'group_mktaggtb':   23, // Activos - Por comentarios → PM Envió CD al Cliente
    'group_mktyejw6':   35, // Certificacion (Miami) → En Certificación Final
    'group_mkty3x75':   35, // Certificacion (Dallas)
    'group_mktad09a':   23, // Procesamiento de Permisos → PM Envió CD al Cliente
    'group_mktatkyf':   38, // Finalizados → Cliente Recibe Conforme
    'group_mktazt64':   -1, // Suspendidos
}

const GROUP_TO_CLIENT_STEP = {
    'topics':           1,  // Activos Preliminar → Producción Preliminar
    'group_mktcbe6z':   6,  // Activos CDs (Miami) → Producción DC
    'group_mktckxx5':   6,  // Activos CDs (Dallas)
    'group_mktaggtb':   7,  // Activos - Por comentarios → Envío DC / Aprobación
    'group_mktyejw6':   9,  // Certificacion (Miami) → Certificación Final
    'group_mkty3x75':   9,  // Certificacion (Dallas)
    'group_mktad09a':   7,  // Procesamiento de Permisos
    'group_mktatkyf':   10, // Finalizados → Entrega Final
    'group_mktazt64':   -1, // Suspendidos
}

// ── Legacy payment disambiguation (Status PM ID 5 only) ─────────────────────
const LEGACY_PAYMENT_PM_BY_GROUP = {
    'topics':           0,  // Activos Preliminar → Bienvenida (first step after transition)
    'group_mktcbe6z':   18, // Activos CDs (Miami) → Solicitud 2do Pago
    'group_mktckxx5':   18, // Activos CDs (Dallas)
    'group_mktaggtb':   32, // Por comentarios → PM Revisa 3er Pago
    'group_mktyejw6':   32, // Certificacion (Miami)
    'group_mkty3x75':   32, // Certificacion (Dallas)
    'group_mktad09a':   32, // Procesamiento de Permisos
}

const LEGACY_PAYMENT_CLIENT_BY_GROUP = {
    'topics':           0,  // Activos Preliminar → Bienvenida
    'group_mktcbe6z':   5,  // Activos CDs (Miami) → Aprobación y 2do Pago
    'group_mktckxx5':   5,  // Activos CDs (Dallas)
    'group_mktaggtb':   8,  // Por comentarios → Último Pago
    'group_mktyejw6':   8,  // Certificacion (Miami)
    'group_mkty3x75':   8,  // Certificacion (Dallas)
    'group_mktad09a':   8,  // Procesamiento de Permisos
}

// ── Phase color resolver ─────────────────────────────────────────────────────

// Estimates board: 2 phases (Marketing 0-5, Venta 6-15)
const ESTIMATE_PHASE_RANGES = [
    { max:  5, phase: 0 }, // Marketing
    { max: 15, phase: 1 }, // Venta
]

// Projects board: 2 phases (Preliminar 0-18, Documentos 19-38)
const PROJECT_PHASE_RANGES = [
    { max: 18, phase: 0 }, // Preliminar
    { max: 38, phase: 1 }, // Documentos
]

const ESTIMATE_PHASE_BADGE_COLORS = [
    'bg-rose-100 text-rose-700',      // 0 — Marketing
    'bg-blue-100 text-blue-700',      // 1 — Venta
]

const PROJECT_PHASE_BADGE_COLORS = [
    'bg-amber-100 text-amber-700',    // 0 — Preliminar
    'bg-emerald-100 text-emerald-700',// 1 — Documentos
]

/**
 * Resolve the CSS badge classes for a project's workflow status.
 * Detects board type from boardId to use correct phase ranges.
 * @param {object} project — { statusWfId, boardId }
 * @returns {string} Tailwind badge classes
 */
export function getStatusPhaseColor(project) {
    if (!project) return 'bg-gray-100 text-gray-500'
    const wfId = project.statusWfId
    if (wfId === null || wfId === undefined) return 'bg-gray-100 text-gray-500'

    const isEstimates = project.boardId === ESTIMATES_BOARD_ID
    const phaseRanges = isEstimates ? ESTIMATE_PHASE_RANGES : PROJECT_PHASE_RANGES
    const badgeColors = isEstimates ? ESTIMATE_PHASE_BADGE_COLORS : PROJECT_PHASE_BADGE_COLORS
    const stepMap     = isEstimates ? ESTIMATE_WF_TO_PM_STEP   : PROJECT_WF_TO_PM_STEP
    const suspendidoId = isEstimates ? null : 40

    if (wfId === suspendidoId) return 'bg-gray-200 text-gray-500'

    const stepIdx = stepMap[wfId]
    if (stepIdx === undefined || stepIdx < 0) return 'bg-gray-200 text-gray-500'

    for (const r of phaseRanges) {
        if (stepIdx <= r.max) return badgeColors[r.phase]
    }
    return 'bg-gray-100 text-gray-500'
}

// ── Public resolver ──────────────────────────────────────────────────────────

/**
 * Resolve the active step index for a Monday item.
 * Uses the new Workflow column, with legacy Status PM fallback.
 * Detects board type from item.boardId to select the correct mapping table.
 * @param {object} item — { statusWfId, statusPmId, groupId, boardId }
 * @param {boolean} isPm — true for PM view, false for client view
 * @returns {number} step index (0-based), or -1 for suspended, 0 if unset
 */
export function resolveActiveStep(item, isPm) {
    if (!item) return 0

    const wfId = item.statusWfId
    const isEstimates = item.boardId === ESTIMATES_BOARD_ID

    // ── Source of truth: Workflow column ──────────────────────────────────
    if (wfId !== null && wfId !== undefined) {
        if (isEstimates) {
            // Estimates board: PM-only flow (no client view)
            const step = ESTIMATE_WF_TO_PM_STEP[wfId]
            if (step !== undefined) return step
        } else {
            const map = isPm ? PROJECT_WF_TO_PM_STEP : PROJECT_WF_TO_CLIENT_STEP
            if (map[wfId] !== undefined) return map[wfId]
        }
    }

    // ── Legacy fallback: Status PM column (Projects board only) ───────────
    if (!isEstimates) {
        const pmId = item.statusPmId
        if (pmId !== null && pmId !== undefined) {
            // Payment disambiguation for legacy Status PM = 5
            if (pmId === 5 && item.groupId) {
                const payMap = isPm ? LEGACY_PAYMENT_PM_BY_GROUP : LEGACY_PAYMENT_CLIENT_BY_GROUP
                if (payMap[item.groupId] !== undefined) return payMap[item.groupId]
            }
            const legacyMap = isPm ? LEGACY_STATUS_TO_PM_STEP : LEGACY_STATUS_TO_CLIENT_STEP
            if (legacyMap[pmId] !== undefined) return legacyMap[pmId]
        }

        // ── Group fallback (when both columns are null) ────────────────────
        if (item.groupId) {
            const groupMap = isPm ? GROUP_TO_PM_STEP : GROUP_TO_CLIENT_STEP
            if (groupMap[item.groupId] !== undefined) return groupMap[item.groupId]
        }
    }

    // No status set → step 0 (beginning)
    return 0
}

/**
 * Parse a dropdown column value → first label ID.
 * Dropdown format: value = '{"ids":[1]}' or null
 * @returns {number|null}
 */
export function parseDropdownId(columnValue) {
    if (!columnValue?.value) return null
    try {
        const parsed = JSON.parse(columnValue.value)
        return parsed?.ids?.[0] ?? null
    } catch {
        return null
    }
}

// Legacy alias
export const parseStatusPmId = parseDropdownId

/**
 * Extract a flat project object from a Monday item.
 * Reads both Status Workflow (primary) and Status PM (fallback).
 */
export function extractProject(item, boardId, boardName) {
    const cols = item.column_values || []
    const wfColId   = STATUS_WORKFLOW_COLUMNS[boardId]
    const wfCol     = wfColId ? cols.find(c => c.id === wfColId) : null
    const statusCol = cols.find(c => c.id === STATUS_PM_COLUMN)
    const emailCol  = cols.find(c => c.id === CLIENT_EMAIL_COL)
    const nameCol   = cols.find(c => c.id === CLIENT_NAME_COL)
    const officeCol = cols.find(c => c.id === OFFICE_COLUMN)
    const pmCol     = cols.find(c => c.id === PM_COLUMN)
    const linkColIds = LINK_COLUMNS[boardId]
    let linkCol = null
    if (Array.isArray(linkColIds)) {
        // Multiple link columns — pick based on office
        const officeText = (officeCol?.text || '').toLowerCase()
        const idx = officeText.includes('dallas') ? 1 : 0
        linkCol = cols.find(c => c.id === linkColIds[idx]) || null
    } else if (linkColIds) {
        linkCol = cols.find(c => c.id === linkColIds) || null
    }

    // Parse link column: value is JSON '{"url":"...","text":"..."}' or null
    let driveLink = ''
    if (linkCol?.value) {
        try {
            const parsed = JSON.parse(linkCol.value)
            driveLink = parsed?.url || ''
        } catch { /* ignore */ }
    }

    // Parse PM people column: text gives name, value has user ID
    let pmName = pmCol?.text || ''
    let pmUserId = null
    if (pmCol?.value) {
        try {
            const parsed = JSON.parse(pmCol.value)
            pmUserId = parsed?.personsAndTeams?.[0]?.id ?? null
        } catch { /* ignore */ }
    }

    /** @type {ArchProject} */
    const result = {
        id:            item.id,
        name:          item.name, // address
        boardId,
        boardName,
        groupId:       item.group?.id || '',
        groupTitle:    item.group?.title || '',
        statusWfId:    parseDropdownId(wfCol),
        statusWfText:  resolveWfLabelKey(boardId, parseDropdownId(wfCol)) || wfCol?.text || '',
        statusPmId:    parseDropdownId(statusCol),
        statusPmText:  statusCol?.text || '',
        clientName:    nameCol?.text || '',
        clientEmail:   (emailCol?.text || '').toLowerCase().trim(),
        office:        officeCol?.text || '',
        driveLink,
        pmName,
        pmUserId,
    }
    warnMissingFields(result, _ARCH_FIELDS, 'ArchProject')
    return result
}
